'use client'

import { ReactNode, useMemo, Children } from 'react'

import { useRules } from './hooks/useRules'

import { RulesContext } from './RulesContext'

import { convertArrayToObject } from './utils'
import { ModalType, UpgradePlanModal } from './components'
import { HomeActionTypes, useHomeStore } from './store'

/**
 *
 * `RulesProvider` is a React provider that provides the rules data to its child components via context.
 * It uses the `useRules` hook to fetch the rules data and the `useMemo` hook to memoize the context value.
 *
 * The `children` prop should be a single React element.
 *
 * @param {Object} props - The props for the component.
 * @param {ReactNode} props.children - The child components.
 * @param {boolean} props.hiddenPlanModal - A boolean that indicates whether the plan modal is hidden.
 *
 */
export function RulesProvider({
  children,
  hiddenPlanModal = false
}: {
  children: ReactNode
  hiddenPlanModal?: boolean
}) {
  const { data } = useRules()
  const { upgradeModal, dispatch } = useHomeStore()

  const values = useMemo(() => {
    return {
      rulesValidations: convertArrayToObject(data ?? [])
    }
  }, [data])

  return (
    <RulesContext.Provider value={values}>
      {!hiddenPlanModal && (
        <UpgradePlanModal
          showModal={upgradeModal.isShow}
          handleShowModal={() => {
            dispatch({
              type: HomeActionTypes.TOGGLE_UPGRADE_MODAL,
              payload: {
                type: ModalType.ADVANCE,
                isShow: false
              }
            })
          }}
          type={upgradeModal.type}
        />
      )}
      {Children.only(children)}
    </RulesContext.Provider>
  )
}

export default RulesProvider
